import { Container, Graphics } from 'pixi.js';

/**
 * Service for managing PixiJS Container and Graphics instances by entity ID.
 * Keeps non-serialisable objects out of ECS components.
 */
export class PixiInstanceService {
  private sprites: Map<number, Container> = new Map();
  private shadows: Map<number, Graphics> = new Map();

  /**
   * Get the Container instance for an entity.
   */
  getSprite(entityId: number): Container | undefined {
    return this.sprites.get(entityId);
  }

  /**
   * Store a Container instance for an entity.
   */
  setSprite(entityId: number, sprite: Container): void {
    this.sprites.set(entityId, sprite);
  }

  /**
   * Delete and return the Container instance for an entity.
   * Returns undefined if no container exists.
   */
  deleteSprite(entityId: number): Container | undefined {
    const sprite = this.sprites.get(entityId);
    if (sprite) {
      this.sprites.delete(entityId);
    }
    return sprite;
  }

  /**
   * Check if an entity has a sprite instance.
   */
  hasSprite(entityId: number): boolean {
    return this.sprites.has(entityId);
  }

  /**
   * Get the shadow Graphics instance for an entity.
   */
  getShadow(entityId: number): Graphics | undefined {
    return this.shadows.get(entityId);
  }

  /**
   * Store a shadow Graphics instance for an entity.
   */
  setShadow(entityId: number, shadow: Graphics): void {
    this.shadows.set(entityId, shadow);
  }

  /**
   * Delete and return the shadow Graphics instance for an entity.
   * Returns undefined if no shadow exists.
   */
  deleteShadow(entityId: number): Graphics | undefined {
    const shadow = this.shadows.get(entityId);
    if (shadow) {
      this.shadows.delete(entityId);
    }
    return shadow;
  }

  /**
   * Check if an entity has a shadow instance.
   */
  hasShadow(entityId: number): boolean {
    return this.shadows.has(entityId);
  }

  /**
   * Destroy all stored sprites and shadows, and clear the internal maps.
   * Should be called when the world is reset to avoid memory leaks.
   */
  clear(): void {
    for (const sprite of this.sprites.values()) {
      sprite.destroy();
    }
    this.sprites.clear();

    for (const shadow of this.shadows.values()) {
      shadow.destroy();
    }
    this.shadows.clear();
  }
}
